import StringUtils from "../utils/StringUtils.js";
import MapInteraction from "../utils/MapInteraction.js";
import { getFlagPath } from "../utils/FlagUtils.js";
import { renderLetterSections } from "../utils/DirectoryMarkup.js";
import { countryPath } from "../utils/SlugUtils.js";

const CONTINENTS = ["af", "eu", "as", "am"];

export default class Training {
    constructor() {
        this.directoryPage = document.querySelector(".page.pays");
        this.mapPage = document.querySelector(".page.training-map");
        this.usMapPage = document.querySelector(".page.training-map-us");

        if (!this.directoryPage && !this.mapPage && !this.usMapPage) return;

        this.countries = [];
        this.searchQuery = "";
        this.activeContinent = "all";

        this.init();
    }

    init() {
        // Le regroupement par lettre est fait par DirectoryMarkup : plus besoin de
        // geoNerdApp.countriesByLetter ici.
        this.countries = window.geoNerdApp.countries;
        this.renderDirectory();
        this.initSearch();
        this.initMap();
        this.initUsMap();
        if (this.directoryPage) {
            window.geoNerdApp.continentsReady.then(() => this.initContinentFilters());
        }

        const searchParams = new URLSearchParams(window.location.search);
        const letter = searchParams.get('letter');
        const countryCode = searchParams.get('country');

        if (this.directoryPage && letter) {
            const section = this.directoryPage.querySelector(`.countries-list .letter-section[data-letter="${letter.toLowerCase()}"]`);
            if (section) {
                section.scrollIntoView({ behavior: "smooth" });
            }
        } else if (this.mapPage && countryCode) {
            this.focusCountry(countryCode);
        } else if (this.mapPage) {
            this.resetMap();
        } else if (this.usMapPage) {
            this.resetUsMap();
        }
    }

    /**
     * Cadre la carte sur un pays donné, depuis `?country=xx`.
     *
     * C'est la cible du lien « explorer sur la carte du monde » des fiches pays :
     * la fiche montre une image fixe, ce lien amène sur la carte réelle, déjà
     * positionnée. Sans le cadrage, le visiteur arriverait sur la mappemonde
     * entière et devrait retrouver le pays lui-même.
     */
    focusCountry(code) {
        const iso = String(code).toLowerCase();
        // Les `id` du SVG sont les codes ISO ; `CSS.escape` n'est pas nécessaire
        // ici mais un code invalide ne doit pas casser le sélecteur.
        if (!/^[a-z]{2}$/.test(iso)) return this.resetMap();

        const path = this.mapSvg && this.mapSvg.querySelector(`#${iso}`);
        const country = this.countries.find(c => c.code.toLowerCase() === iso);
        if (!path || !country) return this.resetMap();

        path.classList.add("active");
        this.showTooltip(country);
        if (this.mapInteraction) this.mapInteraction.zoomToElement(path);
    }

    /**
     * L'annuaire est normalement écrit dans le HTML au build (tâche gulp
     * `prerenderDirectory`, pour qu'il existe sans JS) : on ne reconstruit alors
     * rien. Ce rendu client ne sert que de repli, et passe par le même
     * générateur de markup pour produire exactement la même structure.
     */
    renderDirectory() {
        if (!this.directoryPage) return;

        const countriesList = this.directoryPage.querySelector(".countries-list");
        if (countriesList.dataset.prerendered === "true") return;

        const locale = window.geoNerdApp.languageManager.currentLang;
        countriesList.innerHTML = renderLetterSections(this.countries, locale);
    }

    initSearch() {
        if (!this.directoryPage) return;
        const searchInput = this.directoryPage.querySelector(".search-input");

        searchInput.addEventListener("input", (e) => {
            this.searchQuery = StringUtils.sanitize(e.target.value);
            this.applyFilters();
        });
    }

    /**
     * Builds the "Tous / Afrique / Europe / ..." continent pills once the
     * code -> continent mapping (continents.json) is loaded, and tags every
     * already-rendered .country-item with its continent so applyFilters()
     * can filter on it.
     */
    initContinentFilters() {
        const filtersContainer = this.directoryPage.querySelector(".continent-filters");
        if (!filtersContainer) return;

        this.directoryPage.querySelectorAll(".country-item").forEach(item => {
            item.dataset.continent = window.geoNerdApp.continents[item.dataset.code] || "";
        });

        const lang = window.geoNerdApp.languageManager;
        const options = [{ code: "all", label: lang.t("training.allContinents") }]
            .concat(CONTINENTS.map(code => ({ code, label: lang.t(`mapExploration.continents.${code}`) })));

        filtersContainer.innerHTML = options.map(o =>
            `<button type="button" class="continent-filter${o.code === "all" ? " active" : ""}" data-continent="${o.code}">${o.label}</button>`
        ).join("");

        filtersContainer.querySelectorAll(".continent-filter").forEach(btn => {
            btn.addEventListener("click", () => {
                this.activeContinent = btn.dataset.continent;
                filtersContainer.querySelectorAll(".continent-filter").forEach(b => b.classList.remove("active"));
                btn.classList.add("active");
                this.applyFilters();
            });
        });
    }

    /** Combines the text search and the continent filter — both must match. */
    applyFilters() {
        const countriesList = this.directoryPage.querySelector(".countries-list");
        const countryItems = countriesList.querySelectorAll(".country-item");
        const sections = countriesList.querySelectorAll(".letter-section");

        countryItems.forEach(item => {
            const matchesQuery = this.searchQuery === "" || item.dataset.name.includes(this.searchQuery);
            const matchesContinent = this.activeContinent === "all" || item.dataset.continent === this.activeContinent;
            item.style.display = (matchesQuery && matchesContinent) ? "flex" : "none";
        });

        sections.forEach(section => {
            const hasVisible = Array.from(section.querySelectorAll(".country-item")).some(i => i.style.display !== "none");
            section.style.display = hasVisible ? "block" : "none";
        });
    }

    initMap() {
        if (!this.mapPage) return;
        this.mapContainer = this.mapPage.querySelector(".map-container");
        this.mapSvg = this.mapPage.querySelector("svg");
        const paths = this.mapSvg.querySelectorAll("path");
        this.tooltip = this.mapPage.querySelector("#map-tooltip");
        this.tooltipMoreLink = this.tooltip.querySelector(".tooltip-more");

        this.mapInteraction = new MapInteraction(this.mapContainer, this.mapSvg, { zoomStep: 0.1 });
        this.loadCountryLinks();

        paths.forEach(path => {
            path.addEventListener("click", (e) => {
                if (this.mapInteraction.dragged) return;
                e.stopPropagation();
                const countryCode = path.id.toLowerCase();
                const country = this.countries.find(c => c.code.toLowerCase() === countryCode);

                if (country) {
                    this.showTooltip(country);
                    paths.forEach(p => p.classList.remove("active"));
                    path.classList.add("active");
                }
            });
        });

        this.mapPage.addEventListener("click", (e) => {
            if (e.target.closest('path') || e.target.closest('#map-tooltip')) return;
            this.tooltip.classList.remove("show");
            paths.forEach(p => p.classList.remove("active"));
        });
    }

    showTooltip(country) {
        const flagContainer = this.tooltip.querySelector(".tooltip-flag");
        const nameEl = this.tooltip.querySelector(".tooltip-name");
        const capitalEl = this.tooltip.querySelector(".tooltip-capital");

        flagContainer.innerHTML = `<img src="${getFlagPath(country.code)}" alt="${country.name}">`;
        nameEl.textContent = country.name;
        capitalEl.textContent = country.capital;

        this.updateTooltipMoreLink(country);

        this.tooltip.classList.add("show");
    }

    /**
     * Charge slugs et pays publiés pour le lien « en savoir plus » de la
     * tooltip — chargé ici plutôt que dans le bootstrap global de `app.js`,
     * seule cette page en a besoin.
     */
    loadCountryLinks() {
        Promise.all([
            fetch("/data/country-slugs.json").then(r => r.json()),
            fetch("/data/country-published.json").then(r => r.json()),
        ]).then(([slugs, published]) => {
            this.countrySlugs = slugs;
            this.publishedCountries = new Set(
                Object.values(published.waves || {}).flatMap(w => w.codes || [])
            );
        }).catch(() => {
            this.countrySlugs = null;
            this.publishedCountries = new Set();
        });
    }

    /**
     * Masqué tant que les slugs ne sont pas chargés ou si la fiche n'est pas
     * encore publiée (vagues de L3) : y pointer serait un 404.
     */
    updateTooltipMoreLink(country) {
        if (!this.tooltipMoreLink) return;
        const iso = country.code.toLowerCase();
        const lang = window.geoNerdApp.languageManager.currentLang;
        const slug = this.countrySlugs && this.countrySlugs[iso] && this.countrySlugs[iso][lang];

        if (slug && this.publishedCountries && this.publishedCountries.has(iso)) {
            this.tooltipMoreLink.href = `${lang === "en" ? "/en/" : "/"}${countryPath(lang, slug)}`;
            this.tooltipMoreLink.hidden = false;
        } else {
            this.tooltipMoreLink.hidden = true;
        }
    }

    resetMap() {
        if (!this.mapPage || !this.mapSvg) return;
        const paths = this.mapSvg.querySelectorAll("path");
        paths.forEach(p => p.classList.remove("active"));
        if (this.tooltip) this.tooltip.classList.remove("show");
        if (this.mapInteraction) this.mapInteraction.reset();
    }

    /**
     * Même principe que `initMap()`, pour la carte des États-Unis : pas de
     * capitale (aucune donnée par État) ni de lien "en savoir plus" (pas de
     * fiche par État), donc pas de `loadCountryLinks()` ici.
     */
    initUsMap() {
        if (!this.usMapPage) return;
        this.usMapContainer = this.usMapPage.querySelector(".map-container");
        this.usMapSvg = this.usMapPage.querySelector("svg");
        const paths = this.usMapSvg.querySelectorAll("path");
        this.usTooltip = this.usMapPage.querySelector("#map-tooltip");

        this.usMapInteraction = new MapInteraction(this.usMapContainer, this.usMapSvg, { zoomStep: 0.1 });

        paths.forEach(path => {
            path.addEventListener("click", (e) => {
                if (this.usMapInteraction.dragged) return;
                e.stopPropagation();
                const stateCode = path.id.toLowerCase();
                const state = window.geoNerdApp.usStates.find(s => s.code === stateCode);

                if (state) {
                    this.showUsTooltip(state);
                    paths.forEach(p => p.classList.remove("active"));
                    path.classList.add("active");
                }
            });
        });

        this.usMapPage.addEventListener("click", (e) => {
            if (e.target.closest('path') || e.target.closest('#map-tooltip')) return;
            this.usTooltip.classList.remove("show");
            paths.forEach(p => p.classList.remove("active"));
        });
    }

    showUsTooltip(state) {
        const flagContainer = this.usTooltip.querySelector(".tooltip-flag");
        const nameEl = this.usTooltip.querySelector(".tooltip-name");

        flagContainer.innerHTML = `<img src="${getFlagPath(`us-${state.code}`)}" alt="${state.name}">`;
        nameEl.textContent = state.name;

        this.usTooltip.classList.add("show");
    }

    resetUsMap() {
        if (!this.usMapPage || !this.usMapSvg) return;
        const paths = this.usMapSvg.querySelectorAll("path");
        paths.forEach(p => p.classList.remove("active"));
        if (this.usTooltip) this.usTooltip.classList.remove("show");
        if (this.usMapInteraction) this.usMapInteraction.reset();
    }
}
